import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/useAuth';
import { LayoutDashboard, FileText, Home, LogOut } from 'lucide-react';
import './AdminStyles.css';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Force light theme for admin pages
  useEffect(() => {
    // Save the current theme
    const currentTheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    const currentDataTheme = document.body.getAttribute('data-theme');
    
    // Force light theme
    document.documentElement.classList.remove('dark');
    document.body.setAttribute('data-theme', 'light');
    
    // Restore theme when component unmounts
    return () => {
      if (currentTheme === 'dark') {
        document.documentElement.classList.add('dark');
      }
      if (currentDataTheme) {
        document.body.setAttribute('data-theme', currentDataTheme);
      }
    };
  }, []);

  // Check if a route is active
  const isActive = (path: string) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white text-black admin-area">
      <header className="bg-primary text-primary-foreground py-4 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">Painel Administrativo</h1>
          <div className="flex items-center space-x-2">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/')}
              className="text-white hover:bg-blue-700"
            >
              <Home size={18} className="mr-2" />
              Voltar ao site
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={signOut}
              className="text-white border-white hover:bg-blue-700"
            >
              <LogOut size={18} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="flex-1 flex">
        <nav className="w-64 bg-gray-100 shadow-inner min-h-screen">
          <div className="p-4">
            <div className="space-y-3">
              <Button 
                variant={isActive('/admin') ? "default" : "secondary"}
                className={`w-full justify-start text-base admin-nav-button ${
                  isActive('/admin') 
                    ? 'bg-blue-600 text-white hover:bg-blue-700 font-medium' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-blue-600 border border-gray-200'
                }`}
                onClick={() => navigate('/admin')}
              >
                <LayoutDashboard size={20} className="mr-3" />
                Dashboard
              </Button>
              <Button 
                variant={isActive('/admin/blog') ? "default" : "secondary"}
                className={`w-full justify-start text-base admin-nav-button ${
                  isActive('/admin/blog') 
                    ? 'bg-blue-600 text-white hover:bg-blue-700 font-medium' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-blue-600 border border-gray-200'
                }`}
                onClick={() => navigate('/admin/blog')}
              >
                <FileText size={20} className="mr-3" />
                Blog Posts
              </Button>
            </div>
          </div>
        </nav>

        <main className="flex-1 bg-white">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
